import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { useConfigurationLicence } from '../hooks';
import { FormModal } from 'components/molecules/Modal';
import { Resolver, useForm } from 'react-hook-form';
import { ILicenceTemplateApplicationProcessDocumentForm } from 'models/licenceConfigurator';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors } from 'theme/colors';
import { FormInput } from 'components/molecules/FormInput';
import useDocumentFormSchema from './schemaValidation';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';

interface ILicenceTemplateApplicationProcessAddDocument {
  handleClose: () => void;
  addSuccess: () => void;
  id: string;
}

const initialFormValues = {
  typeUniqueId: '',
  importantNotice: '',
};

const LicenceTemplateApplicationProcessAddDocument = ({
  handleClose,
  addSuccess,
  id,
}: ILicenceTemplateApplicationProcessAddDocument) => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { addApplicationProccessDocument, loadDictionaries, attachmentDocumentTypeData } =
    useConfigurationLicence();
  const { schema } = useDocumentFormSchema();
  const { clearAlert, documentAlert } = useAlert();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.attachmentDocumentTypeData]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const close = () => {
    reset(initialFormValues);
    if (documentAlert && documentAlert.variant === EAlertVariants.error) {
      clearAlert(ActionTypes.CLEAR_DOCUMENTS_ALERT);
    }
    handleClose();
  };

  useEffect(
    () => {
      clearAlert(ActionTypes.CLEAR_DOCUMENTS_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ILicenceTemplateApplicationProcessDocumentForm>({
    defaultValues: initialFormValues,
    resolver: yupResolver(
      schema,
    ) as unknown as Resolver<ILicenceTemplateApplicationProcessDocumentForm>,
  });

  const handleAdd = async (data: ILicenceTemplateApplicationProcessDocumentForm) => {
    const res = await addApplicationProccessDocument({ ...data, applicationProcessId: id });
    if (res) {
      addSuccess();
      close();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleAdd)}>
      <FormModal
        open={true}
        onClickClose={() => {
          close();
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(handleAdd)}
        approveButtonLabel={t('general.save.button')}
      >
        <Grid sx={{ gap: '4px' }}>
          <Grid sx={{ gap: 1.5, flexDirection: 'column', mb: 2.5 }}>
            <Typography variant="h5" sx={{ color: colors.primary500 }}>
              {t('licence.addDocument.label')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(attachmentDocumentTypeData)}
              setValue={setValue}
              name="typeUniqueId"
              label={t('licence.addDocument.type.label')}
              control={control}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="importantNotice"
              label={t('licence.addDocument.importantNotice.label')}
              multiline
              setValue={setValue}
              minRows={6}
              control={control}
              errors={errors}
              required
              charsLimit={255}
              showCounter
            />
          </Grid>
          {documentAlert && documentAlert.variant === EAlertVariants.error && (
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Alert text={documentAlert.text} variant={documentAlert.variant} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </FormModal>
    </form>
  );
};

export { LicenceTemplateApplicationProcessAddDocument };
